import React, { useContext } from "react";

// Third-party
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

// Material UI
import { makeStyles } from "@material-ui/core/styles";

// Utils
import { OLMapStyles } from "utils/OpenLayers/OLMapStyles";
import usePickPoint2D from "hooks/usePickPoint2D/usePickPoint2D";
import usePickPoint3D from "hooks/usePickPoint3D/usePickPoint3D";

const DispatchContext = React.createContext();
const StateContext = React.createContext();

const useStyles = makeStyles(theme => ({
  oltooltip: {
    color: "white",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    marginTop: "15%",
    maxWidth: "350px"
  }
}));

const initialState = {
  coordinates: null,
  layerName: "",
  layerId: null,
  pointDialogOpen: false,
  pointCoordinates: null
};

/**
 * List of available tasks to interact with the IdentifyContext
 * @param  {object} state  states of the context
 * @param  {object} action object with the information to perform a change
 * @return changes the state
 */

function identifyReducer(state, action) {
  switch (action.type) {
    case "COORDINATES_SET": {
      return {
        ...state,
        coordinates: [...action.payload.coordinates],
        layerName: action.payload.layerName,
        layerId: action.payload.layerId
      };
    }

    case "POINT_DIALOG_OPEN": {
      return {
        ...state,
        pointDialogOpen: true,
        pointCoordinates: {
          lon: action.payload.lon,
          lat: action.payload.lat
        }
      };
    }

    case "POINT_DIALOG_CLOSE": {
      return {
        ...state,
        pointDialogOpen: false,
        pointCoordinates: null
      };
    }

    case "LAYER_NAME_CLEAR": {
      return {
        ...state,
        layerName: null
      };
    }

    case "STATE_RESET": {
      return {
        ...state,
        coordinates: null,
        layerName: "",
        layerId: null,
        pointDialogOpen: false,
        pointCoordinates: null
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function IdentifyProvider({ children }) {
  //provides all children with the state and the dispatch
  const [state, dispatch] = React.useReducer(identifyReducer, initialState);
  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
}

//The methods and state that the children can do with the IdentifyContext
function useIdentifyContext() {
  const identifyState = useContext(StateContext);
  const identifyDispatch = useContext(DispatchContext);
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const pointStyle = React.useMemo(
    () => new OLMapStyles().drawIdentifyAttributePoint,
    []
  );

  if (!identifyState || !identifyDispatch) {
    throw new Error(
      "useIdentifyContext must be used within a IdentifyProvider"
    );
  }
  // Store current layer info for callbacks
  const currentLayerNameRef = React.useRef(null);
  const currentLayerIdRef = React.useRef(null);
  const currentIdentifyTypeRef = React.useRef(null);
  const currentIsPointRef = React.useRef(false);

  // 2D hook integration
  const {
    isActive: isPicking2D,
    startPickPoint: startPickPoint2D,
    stopPickPoint: stopPickPoint2D,
    clearPoint: clearPoint2D
  } = usePickPoint2D({
    style: pointStyle,
    condition: e => e.originalEvent.buttons === 1,
    tooltipClassName: classes.oltooltip,
    getHelpMessage: () =>
      createHelpMsg(
        currentIdentifyTypeRef.current || "",
        currentLayerNameRef.current || ""
      ),
    onCancel: () => {
      identifyDispatch({ type: "STATE_RESET" });
    },
    onEnd: geojsonPoint => {
      identifyDispatch({
        type: "COORDINATES_SET",
        payload: {
          coordinates: geojsonPoint.coordinates,
          layerName: currentLayerNameRef.current,
          layerId: currentLayerIdRef.current
        }
      });

      if (currentIsPointRef.current) {
        identifyDispatch({
          type: "POINT_DIALOG_OPEN",
          payload: {
            lon: geojsonPoint.coordinates[0],
            lat: geojsonPoint.coordinates[1]
          }
        });
      }
    }
  });

  // 3D hook integration
  const {
    isActive: isPicking3D,
    startPickPoint: startPickPoint3D,
    stopPickPoint: stopPickPoint3D,
    clearPoint: clearPoint3D
  } = usePickPoint3D({
    getHelpMessage: () =>
      createHelpMsg(
        currentIdentifyTypeRef.current || "",
        currentLayerNameRef.current || ""
      ),
    onCancel: () => {
      identifyDispatch({ type: "STATE_RESET" });
    },
    onEnd: geojsonPoint => {
      identifyDispatch({
        type: "COORDINATES_SET",
        payload: {
          coordinates: geojsonPoint.coordinates,
          layerName: currentLayerNameRef.current,
          layerId: currentLayerIdRef.current
        }
      });

      if (currentIsPointRef.current) {
        identifyDispatch({
          type: "POINT_DIALOG_OPEN",
          payload: {
            lon: geojsonPoint.coordinates[0],
            lat: geojsonPoint.coordinates[1]
          }
        });
      }
    }
  });
  /**
   * Starts the interaction with the 2D map (OL), using the 2D hook.
   * Accepts a single payload object to avoid positional argument mismatch.
   *
   * @param {{
   *   layerName: string,
   *   layerId: (string|number|null),
   *   identifyType: string,
   *   isPoint?: boolean
   * }} payload
   */
  function launchIdentifyInteraction2D({
    layerName,
    layerId,
    identifyType,
    isPoint = false
  }) {
    if (isPicking2D) {
      enqueueSnackbar(t("IdentifyDlg.StopInteraction"), {
        variant: "warning"
      });
      return;
    }

    currentLayerNameRef.current = layerName;
    currentLayerIdRef.current = layerId;
    currentIdentifyTypeRef.current = identifyType;
    currentIsPointRef.current = isPoint;

    clearPoint2D();
    identifyDispatch({ type: "STATE_RESET" });

    const { started, reason } = startPickPoint2D();

    if (!started && reason === "busy") {
      enqueueSnackbar(t("IdentifyDlg.StopInteraction"), {
        variant: "warning"
      });
    }
  }

  /**
   * Creates a new help tooltip.
   */
  function createHelpMsg(identifyType, layerName) {
    const helpMsg =
      t("IdentifyDlg.IdentifyTooltip1Click") +
      identifyType +
      t("IdentifyDlg.IdentifyTooltip2In") +
      layerName +
      t("IdentifyDlg.IdentifyTooltip3Enter") +
      t("IdentifyDlg.IdentifyTooltip4Cancel");

    return helpMsg;
  }

  /**
   * Starts the interaction with the 3D map (Cesium), using the 3D hook.
   * Accepts a single payload object to avoid positional argument mismatch.
   *
   * @param {{
   *   layerName: string,
   *   layerId: (string|number|null),
   *   identifyType: string,
   *   isPoint?: boolean
   * }} payload
   */
  function launchIdentifyInteraction3D({
    layerName,
    layerId,
    identifyType,
    isPoint = false
  }) {
    if (isPicking3D) {
      enqueueSnackbar(t("IdentifyDlg.StopInteraction"), {
        variant: "warning"
      });
      return;
    }

    currentLayerNameRef.current = layerName;
    currentLayerIdRef.current = layerId;
    currentIdentifyTypeRef.current = identifyType;
    currentIsPointRef.current = isPoint;

    clearPoint3D();

    const { started, reason } = startPickPoint3D();

    if (!started && reason === "busy") {
      enqueueSnackbar(t("IdentifyDlg.StopInteraction"), {
        variant: "warning"
      });
    }
  }

  /**
   * Clear layer name
   */
  function clearLayerName() {
    identifyDispatch({ type: "LAYER_NAME_CLEAR" });
  }

  /**
   * Reset the identify context state
   */
  function resetIdentifyState() {
    stopPickPoint2D();
    stopPickPoint3D();
    clearPoint2D();
    clearPoint3D();
    currentLayerNameRef.current = null;
    currentLayerIdRef.current = null;
    currentIdentifyTypeRef.current = null;
    currentIsPointRef.current = false;
    identifyDispatch({ type: "STATE_RESET" });
  }

  /**
   * Close identify point floating dialog.
   */
  function closeIdentifyPointDialog() {
    identifyDispatch({ type: "POINT_DIALOG_CLOSE" });
  }

  // Backward-compatible wrappers for older callers still using positional args.
  function launchIdentifyIteraction2D(
    layerName,
    layerId,
    identifyType,
    isPoint
  ) {
    launchIdentifyInteraction2D({
      layerName,
      layerId,
      identifyType,
      isPoint
    });
  }

  function launchIdentifyIteraction3D(
    layerName,
    layerId,
    identifyType,
    isPoint
  ) {
    launchIdentifyInteraction3D({
      layerName,
      layerId,
      identifyType,
      isPoint
    });
  }

  return {
    identifyState,
    launchIdentifyInteraction2D,
    launchIdentifyInteraction3D,
    launchIdentifyIteraction2D,
    launchIdentifyIteraction3D,
    clearLayerName,
    resetIdentifyState,
    closeIdentifyPointDialog
  };
}

IdentifyProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { useIdentifyContext, identifyReducer };
export default IdentifyProvider;
